/**
 * Shot-cut detection via per-frame perceptual hash + twin-comparison.
 *
 * Feature: dHash. Each frame is sampled at a 9x8 luma grid; for every row we
 * compare each cell to its right neighbor and emit a bit. That gives a 64-bit
 * fingerprint that captures structure (light-on-left vs light-on-right, etc.)
 * rather than absolute color, so it tolerates exposure/white-balance drift but
 * still flips heavily when the scene actually changes. Distance between two
 * frames is Hamming distance normalized to [0, 1].
 *
 * Detector: twin-comparison (Zhang et al.). One threshold is too crude — a
 * single high cutoff misses cross-fades and dissolves where each frame-to-
 * frame change is small but the cumulative change over a window is large.
 * So we run two:
 *
 *   - HARD_CUT_THRESHOLD — if a single inter-frame distance crosses this, it's
 *     a hard cut and we emit immediately.
 *   - GRADUAL_LOW_THRESHOLD — distances above this open an accumulation
 *     window. We sum distances while staying above the noise floor. If the
 *     sum crosses GRADUAL_HIGH_THRESHOLD within GRADUAL_WINDOW_FRAMES, we
 *     emit a gradual cut at the current frame. If the window expires or the
 *     distance falls back below the noise floor first, we reset without
 *     emitting.
 *
 * Cut placement: for both kinds, the cut is recorded at the frame where the
 * decision fires (end of a fade, not the start), so the shot that follows
 * begins on steady-state content.
 *
 * Known limitation: a long slow pan can produce sustained low-level distances
 * that creep the accumulator up to the high threshold. The frame-window cap
 * bounds this but doesn't eliminate it; thresholds want empirical tuning
 * against a real reference-clip corpus.
 */

export interface Sample {
    timeS: number;
    canvas: HTMLCanvasElement;
}

export interface DetectShotsResult {
    cutTimestampsS: number[];
    representativeIndices: number[];
}

const HASH_WIDTH = 9;
const HASH_HEIGHT = 8;
const HASH_BYTES = 8;

const HARD_CUT_THRESHOLD = 0.4;
const GRADUAL_LOW_THRESHOLD = 0.08;
const GRADUAL_HIGH_THRESHOLD = 0.4;
const GRADUAL_WINDOW_FRAMES = 5;

const POPCOUNT_TABLE = (() => {
    const t = new Uint8Array(256);
    for (let i = 0; i < 256; i++) {
        let n = i;
        let c = 0;
        while (n) {
            c += n & 1;
            n >>>= 1;
        }
        t[i] = c;
    }
    return t;
})();

function luma(data: Uint8ClampedArray, x: number, y: number, width: number): number {
    const i = (y * width + x) * 4;
    return 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
}

function buildDHash(canvas: HTMLCanvasElement): Uint8Array {
    const ctx = canvas.getContext('2d');
    if (!ctx) {
        throw new Error('Could not acquire 2D context for shot detection');
    }
    const { data, width, height } = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const xs: number[] = [];
    for (let i = 0; i < HASH_WIDTH; i++) {
        xs.push(Math.min(width - 1, Math.floor(((i + 0.5) * width) / HASH_WIDTH)));
    }
    const ys: number[] = [];
    for (let i = 0; i < HASH_HEIGHT; i++) {
        ys.push(Math.min(height - 1, Math.floor(((i + 0.5) * height) / HASH_HEIGHT)));
    }
    const hash = new Uint8Array(HASH_BYTES);
    let bit = 0;
    for (let yi = 0; yi < HASH_HEIGHT; yi++) {
        for (let xi = 0; xi < HASH_WIDTH - 1; xi++) {
            const left = luma(data, xs[xi], ys[yi], width);
            const right = luma(data, xs[xi + 1], ys[yi], width);
            if (left > right) {
                hash[bit >> 3] |= 1 << (bit & 7);
            }
            bit++;
        }
    }
    return hash;
}

function hammingDistance(a: Uint8Array, b: Uint8Array): number {
    let d = 0;
    for (let i = 0; i < a.length; i++) d += POPCOUNT_TABLE[a[i] ^ b[i]];
    return d / (HASH_BYTES * 8);
}

export function detectShots(samples: Sample[]): DetectShotsResult {
    if (samples.length === 0) {
        return { cutTimestampsS: [], representativeIndices: [] };
    }
    const hashes = samples.map((s) => buildDHash(s.canvas));

    const cutIndices: number[] = [];
    let candidateStart: number | null = null;
    let accumulator = 0;

    for (let i = 1; i < samples.length; i++) {
        const d = hammingDistance(hashes[i - 1], hashes[i]);

        if (d >= HARD_CUT_THRESHOLD) {
            cutIndices.push(i);
            candidateStart = null;
            accumulator = 0;
            continue;
        }

        if (d >= GRADUAL_LOW_THRESHOLD) {
            if (candidateStart === null) {
                candidateStart = i;
                accumulator = d;
                continue;
            }
            accumulator += d;
            if (i - candidateStart >= GRADUAL_WINDOW_FRAMES) {
                candidateStart = null;
                accumulator = 0;
                continue;
            }
            if (accumulator >= GRADUAL_HIGH_THRESHOLD) {
                cutIndices.push(i);
                candidateStart = null;
                accumulator = 0;
            }
            continue;
        }

        candidateStart = null;
        accumulator = 0;
    }

    const shotBoundaries = [0, ...cutIndices, samples.length];
    const representativeIndices: number[] = [];
    for (let s = 0; s < shotBoundaries.length - 1; s++) {
        const start = shotBoundaries[s];
        const endExclusive = shotBoundaries[s + 1];
        representativeIndices.push(Math.floor((start + endExclusive - 1) / 2));
    }

    return {
        cutTimestampsS: cutIndices.map((i) => samples[i].timeS),
        representativeIndices,
    };
}
